
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Registration {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  course: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

export const useAdminData = () => {
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<Record<string, string>>({});
  const { toast } = useToast();

  const fetchRegistrations = async () => {
    try {
      const { data, error } = await supabase
        .from('registrations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Type assertion to ensure status is properly typed
      const typedRegistrations = (data || []).map(reg => ({
        ...reg,
        status: reg.status as 'pending' | 'approved' | 'rejected'
      }));

      setRegistrations(typedRegistrations);
    } catch (error) {
      console.error('Error fetching registrations:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել գրանցումները",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('key, value')
        .in('key', ['admission_deadline', 'course_start_date', 'group_price', 'individual_price']);

      if (error) throw error;

      const settingsMap = (data || []).reduce((acc, setting) => {
        acc[setting.key] = setting.value;
        return acc;
      }, {} as Record<string, string>);

      setSettings(settingsMap);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const updateRegistrationStatus = async (id: string, status: 'pending' | 'approved' | 'rejected'): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('registrations')
        .update({ status })
        .eq('id', id);

      if (error) throw error;

      // Update local state
      setRegistrations(prev =>
        prev.map(reg =>
          reg.id === id ? { ...reg, status } : reg
        )
      );

      toast({
        title: "Հաջողություն",
        description: "Կարգավիճակը հաջողությամբ թարմացվեց"
      });

      return true;
    } catch (error) {
      console.error('Error updating registration status:', error);
      toast({
        title: "Սխալ",
        description: "Կարգավիճակի թարմացումը ձախողվեց",
        variant: "destructive"
      });
      return false;
    }
  };

  const getSetting = (key: string): string => {
    return settings[key] || '';
  };

  const updateSetting = async (key: string, value: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('settings')
        .upsert({ key, value }, { onConflict: 'key' });

      if (error) throw error;

      // Update local settings
      setSettings(prev => ({ ...prev, [key]: value }));

      toast({
        title: "Հաջողություն",
        description: "Կարգավորումները հաջողությամբ թարմացվեցին"
      });

      return true;
    } catch (error) {
      console.error('Error updating setting:', error);
      toast({
        title: "Սխալ",
        description: "Կարգավորումների թարմացումը ձախողվեց",
        variant: "destructive"
      });
      return false;
    }
  };

  const deleteRegistration = async (registrationId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('registrations')
        .delete()
        .eq('id', registrationId);

      if (error) throw error;

      // Update local state
      setRegistrations(prev => prev.filter(reg => reg.id !== registrationId));

      toast({
        title: "Հաջողություն",
        description: "Հայտը հաջողությամբ ջնջվեց"
      });

      return true;
    } catch (error) {
      console.error('Error deleting registration:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց ջնջել հայտը",
        variant: "destructive"
      });
      return false;
    }
  };

  useEffect(() => {
    fetchRegistrations();
    fetchSettings();
  }, []);

  return {
    registrations,
    updateRegistrationStatus,
    deleteRegistration,
    getSetting,
    updateSetting,
    loading
  };
};
